<template>
  <div class="flex flex-col items-start">
    <div
      v-if="tierPrices.length > 1"
      class="mb-6 w-full bg-gray-10 p-5 2xl:w-1/2"
    >
      <span class="mb-2 block font-bold text-gray-500">
        {{ $t('product.tierPrices') }}
      </span>
      <div class="flex flex-col divide-y divide-gray-100 text-sm text-gray-400">
        <div
          v-for="tierPrice in tierPrices"
          :key="tierPrice.label"
          class="grid grid-cols-2 py-1 lg:grid-cols-3"
        >
          <span class="font-medium">
            {{ getTranslatedTierPriceLabel(tierPrice) }}
          </span>
          <ShopSharedPrice
            :value="tierPrice.unitPrice"
            class="font-bold lg:col-span-2"
          />
        </div>
      </div>
    </div>

    <div class="flex items-center gap-4">
      <div v-if="priceVisible" class="flex flex-col">
        <ShopSharedPrice
          v-if="total"
          class="text-md font-bold lg:text-2xl"
          :class="{ 'text-red-600': isListPrice }"
          :value="total"
        />
        <ShopSharedPrice
          v-if="isListPrice"
          class="text-xs font-medium text-gray-300 line-through"
          :value="baseTotal"
        />
        <span class="mt-0 inline-block text-xs text-gray-300">
          <template v-if="isNetCustomer">
            {{ $t('product.netPriceInfo') }}
          </template>
          <template v-else>
            {{ $t('product.grossPriceInfo') }}
          </template>
        </span>
      </div>

      <div
        v-if="b2cPrice"
        class="flex flex-col gap-1 border-l border-gray-100 py-2 pl-4"
      >
        <span
          class="flex items-center gap-1 text-2xs font-medium text-gray-400"
        >
          {{ $t('product.listPrice') }}
          <IconInfo
            class="inline-block size-3 rounded-full bg-black p-0.5 text-white"
          />
        </span>
        <span class="inline-flex items-baseline gap-1">
          <ShopSharedPrice
            v-if="product.customFields?.novu_jp_custom_products_uvp_price"
            :value="product.customFields?.novu_jp_custom_products_uvp_price"
            class="text-sm font-bold"
            :class="{ 'text-red-600': b2cPrice.listPrice }"
          />

          <ShopSharedPrice
            v-if="
              product.customFields
                ?.novu_jp_custom_products_uvp_strike_through_price
            "
            :value="
              product.customFields
                ?.novu_jp_custom_products_uvp_strike_through_price
            "
            class="text-2xs font-medium text-gray-300 line-through"
          />
        </span>
      </div>
      <div
        v-else-if="showB2BPriceText"
        class="flex max-w-96 gap-2 border-l border-gray-100 pl-4"
      >
        <div
          class="mt-0.5 flex size-3.5 shrink-0 items-center justify-center rounded-full border border-black p-0.5"
        >
          <IconInfo class="h-full" />
        </div>
        <div class="flex flex-col gap-1 text-xs">
          <span class="font-semibold text-black">
            {{ $t('product.professionalPriceTitle') }}
          </span>
          <span class="font-medium text-gray-300">
            {{ $t('product.professionalPriceText') }}
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="!priceVisible"
      class="flex max-w-96 gap-2 border border-gray-100 p-3"
    >
      <div
        class="mt-0.5 flex size-3.5 shrink-0 items-center justify-center rounded-full border border-black p-0.5"
      >
        <IconInfo class="h-full" />
      </div>
      <span class="text-xs font-bold">
        {{ $t('product.professionalOnlyText') }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TierPrice } from '@shopware/helpers'
import type { Schemas } from '#shopware'
import { ProfessionalCustomerGroupId } from '~/types/general'

const props = withDefaults(
  defineProps<{
    product: Schemas['Product']
    quantity?: number
  }>(),
  {
    quantity: 1,
  }
)

const { t } = useI18n()
const { product } = toRefs(props)
const { user } = useUser()
const { total, baseTotal, isListPrice, tierPrices, b2cPrice } = useDynamicPrice(
  product,
  toRef(props, 'quantity')
)

const priceVisible = computed(() => {
  return product.value.extensions?.hiddenPrice?.isHiddenPrice !== true
})

const showB2BPriceText = computed(() => {
  return (
    user.value?.groupId !== ProfessionalCustomerGroupId && priceVisible.value
  )
})

const getTranslatedTierPriceLabel = (tierPrice: TierPrice) => {
  if (tierPrice.label.startsWith('from')) {
    return t('product.tierPriceFrom', { quantity: tierPrice.quantity })
  } else {
    return t('product.tierPriceTo', { quantity: tierPrice.quantity })
  }
}

const isNetCustomer = computed(
  () => user.value?.groupId === ProfessionalCustomerGroupId
)
</script>
